// ATEM SuperSource geometry: protocol values <-> output pixels.
//
// This is the single source of truth for where a SuperSource box lands on the
// output. The bridge uses it to turn layouts and playout geometry into protocol
// values, and the browser uses it to draw a pixel-exact preview.
//
// Everything here was measured on the studio's ATEM Constellation 8K (1080p50)
// from frame captures; see docs/atem-supersource-mapping.md for the method and
// the raw data. All pixel values are for a 1920x1080 output, in pixel-edge
// coordinates (a box with left = 0 and right = 2 covers columns 0 and 1).
//
// The short version:
// - Positions are 0.6 px per unit, +x right, +y UP, 0/0 = frame centre.
// - Size is clamped to 70..1000. The window is W = 2*floor(kfp*960/65536)
//   wide (kfp = size in 16.16 fixed point) and H = fe(W*9/16) tall, and the
//   picture is scaled by k = W/1920 on both axes.
// - Every window edge lands on an even pixel. The switcher computes the edges
//   in float32 from a 16.16 position; reproducing that chain step by step
//   (Math.fround) gives every measured edge exactly, including the ones that
//   sit exactly on an even pixel and land 2 px further left/up.
// - Crop is in SOURCE pixels (0.06 px per unit: top/bottom 0..18000 = 1080 px,
//   left/right 0..32000 = 1920 px). It moves the window edges inward; the
//   picture never moves. The crop amount is float32 as well.
// - Every box has a soft edge against the art: the 50 % point sits 1.533 px
//   inside the window (0.5 px when the border is on), so even a size 1000 box
//   at 0/0 leaves a 1-2 px frame of background. Box over box is hard.
// - Box 1 (index 0) is drawn on top.
/** Output frame the measurements were taken at. */
export const SS_FRAME_WIDTH = 1920;
export const SS_FRAME_HEIGHT = 1080;
/** Output pixels per position unit, on both axes. */
export const SS_PX_PER_POSITION_UNIT = 0.6;
/** Position that puts the box centre on the right (x) / top (y) frame edge. */
export const SS_POSITION_X_EDGE = 1600;
export const SS_POSITION_Y_EDGE = 900;
/** Range the switcher accepts (it clamps and stores anything beyond). */
export const SS_POSITION_X_LIMIT = 4800;
export const SS_POSITION_Y_LIMIT = 3400;
/** Size range the switcher accepts. Lower values are stored as 70. */
export const SS_SIZE_MIN = 70;
export const SS_SIZE_MAX = 1000;
/** Crop ranges: both are 0.06 source px per unit, so the full source frame. */
export const SS_CROP_TB_MAX = 18000;
export const SS_CROP_LR_MAX = 32000;
export const SS_CROP_SOURCE_PX_PER_UNIT = 0.06;
/** Border width range; 0.3 output px per unit, whatever the box size. */
export const SS_BORDER_WIDTH_MAX = 1600;
export const SS_BORDER_PX_PER_UNIT = 0.3;
/**
 * Soft edge with the border off, as the box's alpha per pixel counting inward
 * from the window edge (index 0 = the first pixel inside the window).
 * Everything further in is fully opaque. Samples of the linear ramp
 * `clamp(0.5 + (d - 1.533) / 2.045)`, d = pixel centre inside the window.
 */
export const SS_EDGE_RAMP = [0, 0.484, 0.973];
/**
 * Same with the border enabled (any width, even 0): pixel L is half border,
 * half picture (alpha 0.5), L+1 0.991. Ramp `clamp(0.5 + (d - 0.5) / 2.04)`.
 */
export const SS_EDGE_RAMP_BORDER = [0.5, 0.991];
/** Alpha of the pixel just outside the window when the border is enabled. */
export const SS_EDGE_OUTSIDE_ALPHA_BORDER = 0.009;
/** Distance of the 50 % edge inside the window (border off). */
export const SS_SOFT_INSET = 1.533;
/** Width of the border-off soft edge ramp (px). */
export const SS_SOFT_RAMP = 2.045;
/** 50 % edge with the border on: where the picture starts at inner width 0. */
export const SS_SOFT_INSET_BORDER = 0.5;
/**
 * How far a window edge has to sit past the frame edge before the frame's
 * outermost pixel is fully covered (border off): pixel L+3 is the first opaque
 * one, so L must be <= -3, i.e. -4 on the even grid. With the border on (no
 * outer border) -2 would do; 4 is safe for both.
 */
export const SS_CLEAN_EDGE_MARGIN = 4;
/**
 * How far (px) a frame-aligned window edge is pushed past the frame so the
 * frame's outermost pixel row or column is plain picture. With the border on
 * that pixel also has to be past the inner border and both softness ramps;
 * the outer border lies outside the window, so pushing the window out moves
 * it off frame as well. Never less than {@link SS_CLEAN_EDGE_MARGIN}.
 */
export function ssCleanEdgeMargin(border) {
    const edges = ssBorderEdges(border);
    if (!edges)
        return SS_CLEAN_EDGE_MARGIN;
    // The centre of pixel 0 is margin + 0.5 inside the window and needs layer
    // alpha 1 and picture share 1 (see ssBorderEdges / ssRampWidth).
    const opaque = Math.max(edges.outer + edges.outerRamp / 2, edges.inner + edges.innerRamp / 2);
    return Math.max(SS_CLEAN_EDGE_MARGIN, Math.ceil(opaque - 0.5 - EPS));
}
const EPS = 1e-6;
/** Floor to an even pixel (tolerant of float noise in px arithmetic). */
export function floorEven(v) {
    return 2 * Math.floor(v / 2 + EPS);
}
/** Round to an even pixel, ties (odd integers) go up. */
export function roundEven(v) {
    return 2 * Math.floor(v / 2 + 0.5);
}
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
// ============ The switcher's float32 chain ============
/** float32 after every single operation, as the switcher computes. */
const f32 = Math.fround;
/** Exact floor to even (no tolerance: the float32 value decides). */
const fe = (v) => 2 * Math.floor(v / 2);
/** Exact round to even, ties (odd integers) up. */
const rhe = (v) => 2 * Math.floor(v / 2 + 0.5);
/**
 * Protocol position (1/100 unit) -> units as the switcher holds it: rounded
 * to 1/65536 unit (16.16 fixed point; ties cannot occur), negative positions
 * one LSB lower. Exact in float32.
 */
export function ssPositionUnits(v) {
    let q = Math.floor((v * 65536) / 100 + 0.5);
    if (v < 0)
        q -= 1;
    return f32(q / 65536);
}
/** Box scale as the switcher holds it: float32(W / 1920). */
function scaleF32(W) {
    return f32(W / SS_FRAME_WIDTH);
}
/**
 * Unquantised left window edge in px from the left frame edge, computed in
 * float32 exactly as the switcher does (the frame is 32 units wide).
 */
export function ssRawLeft(x, W) {
    const X = ssPositionUnits(x);
    const k = scaleF32(W);
    return f32(f32(f32(f32(X - f32(16 * k)) + 16) / 32) * 1920);
}
/**
 * Unquantised top window edge in px from the top frame edge (y is up; the
 * frame is 18 units tall), float32 like the switcher.
 */
export function ssRawTop(y, W) {
    const Y = ssPositionUnits(y);
    const k = scaleF32(W);
    return f32(f32(f32(9 - f32(Y + f32(9 * k))) / 18) * 1080);
}
/**
 * Where an unquantised window edge lands: in-frame edges floor to even;
 * edges left of / above the frame round to even, exact odd values away from
 * zero. Deterministic: there is no boundary ambiguity once the edge comes from
 * {@link ssRawLeft} / {@link ssRawTop}.
 */
export function ssQuantiseEdge(v) {
    return v >= 0 ? fe(v) : -2 * Math.floor(-v / 2 + 0.5);
}
/**
 * Crop amount in output px (before quantising) for a window W px wide. The
 * crop is rounded to 1/65536 unit, scaled by float32(W/1920), then converted
 * to px in float32: horizontally `u*60`, vertically `(u*1080)/18`. The
 * different order matters for crop products that are exact integers.
 */
export function ssCropPx(units, W, axis) {
    const cq = Math.floor((units * 65536) / 1000 + 0.5); // never a tie
    const u = f32(f32(cq / 65536) * scaleF32(W));
    return axis === "h" ? f32(u * 60) : f32(f32(u * 1080) / 18);
}
export const SS_BEVEL_NONE = 0;
export const SS_BEVEL_IN_OUT = 1;
export const SS_BEVEL_IN = 2;
export const SS_BEVEL_OUT = 3;
/** Protocol ranges of the border fields (stored values, measured). */
export const SS_BORDER_RANGES = {
    outerWidth: [0, 1600],
    innerWidth: [0, 1600],
    outerSoftness: [0, 100],
    innerSoftness: [0, 100],
    bevel: [0, 3],
    bevelPosition: [0, 100],
    bevelSoftness: [0, 100],
    hue: [0, 3599],
    saturation: [0, 1000],
    luma: [0, 1000],
    lightSourceDirection: [0, 3599],
    lightSourceAltitude: [10, 100],
};
/** Window width for a size value (16.16 fixed point, then even pixels). */
export function ssWindowWidth(size) {
    const s = clamp(Math.round(size), SS_SIZE_MIN, SS_SIZE_MAX);
    const kfp = Math.round((s * 65536) / 1000);
    return 2 * Math.floor((kfp * 960) / 65536);
}
/** Window height for a window width: the picture height floored to even. */
export function ssWindowHeight(width) {
    return 2 * Math.floor((width * 9) / 32);
}
/** Border width in protocol units -> px. 0.3 px per unit, snapped up to 0.5 px. */
export function ssBorderWidthPx(units) {
    const w = clamp(Math.round(units), 0, SS_BORDER_WIDTH_MAX);
    return Math.ceil((w * 3) / 5) / 2;
}
/**
 * Width (px) of an edge ramp for a softness of 0..100: 2.04 px at 0, centred
 * on the hard edge. Fitted on 63 softness values (max error 0.11 px).
 */
export function ssRampWidth(softness) {
    const s = clamp(Math.round(softness), 0, 100);
    return 2.04 + 0.02553 * s * s;
}
/**
 * Where the border's edges sit, as inside distances of the cropped window
 * (null with the border off):
 * - outer: the outer border edge (layer alpha 50 %), at most 0.5
 * - inner: where the picture starts (picture share 50 %), at least 0.5
 */
export function ssBorderEdges(border) {
    if (!border?.enabled)
        return null;
    const wo = ssBorderWidthPx(border.outerWidth ?? 0);
    const wi = ssBorderWidthPx(border.innerWidth ?? 0);
    return {
        outer: Math.min(1 - wo, 0.5),
        inner: Math.max(wi, 0.5),
        outerRamp: ssRampWidth(border.outerSoftness ?? 0),
        innerRamp: ssRampWidth(border.innerSoftness ?? 0),
    };
}
/**
 * Where a box lands on the 1080p output. Pixel exact (validated on 59,107
 * captured window edges with no mismatch).
 */
export function ssBoxGeometry(box, border) {
    const size = clamp(Math.round(box.size), SS_SIZE_MIN, SS_SIZE_MAX);
    const W = ssWindowWidth(size);
    const H = ssWindowHeight(W);
    const k = W / SS_FRAME_WIDTH;
    const x = clamp(Math.round(box.x), -SS_POSITION_X_LIMIT, SS_POSITION_X_LIMIT);
    const y = clamp(Math.round(box.y), -SS_POSITION_Y_LIMIT, SS_POSITION_Y_LIMIT);
    const L = ssQuantiseEdge(ssRawLeft(x, W));
    const T = ssQuantiseEdge(ssRawTop(y, W));
    const bounds = { left: L, top: T, right: L + W, bottom: T + H };
    let window = { ...bounds };
    let empty = box.enabled === false;
    if (box.cropped) {
        const c = (v, max) => clamp(Math.round(v ?? 0), 0, max);
        const ct = c(box.cropTop, SS_CROP_TB_MAX);
        const cb = c(box.cropBottom, SS_CROP_TB_MAX);
        const cl = c(box.cropLeft, SS_CROP_LR_MAX);
        const cr = c(box.cropRight, SS_CROP_LR_MAX);
        window = {
            left: L + fe(ssCropPx(cl, W, "h")),
            top: T + fe(ssCropPx(ct, W, "v")),
            right: bounds.right - rhe(ssCropPx(cr, W, "h")),
            bottom: bounds.bottom - rhe(ssCropPx(cb, W, "v")),
        };
        if (cl + cr >= SS_CROP_LR_MAX || ct + cb >= SS_CROP_TB_MAX)
            empty = true;
    }
    if (window.right <= window.left || window.bottom <= window.top)
        empty = true;
    const edges = ssBorderEdges(border);
    const outerBorder = edges
        ? {
            left: window.left + edges.outer,
            top: window.top + edges.outer,
            right: window.right - edges.outer,
            bottom: window.bottom - edges.outer,
        }
        : null;
    return {
        size,
        width: W,
        height: H,
        scale: k,
        bounds,
        window,
        empty,
        picture: {
            left: L + 0.5 * (1 - k),
            top: T - 0.45 * (1 - k),
            width: SS_FRAME_WIDTH * k,
            height: SS_FRAME_HEIGHT * k,
        },
        visibleInset: edges ? edges.outer : SS_SOFT_INSET,
        pictureInset: edges ? edges.inner : SS_SOFT_INSET,
        outerBorder,
        innerBorderWidth: edges ? ssBorderWidthPx(border?.innerWidth ?? 0) : 0,
    };
}
/**
 * The visible (50 % alpha) edges of a box, clipped to the frame. This is what
 * a capture measures; `null` when nothing of the box is on screen.
 */
export function ssVisibleRect(geometry, frameWidth = SS_FRAME_WIDTH, frameHeight = SS_FRAME_HEIGHT) {
    if (geometry.empty)
        return null;
    const i = geometry.visibleInset;
    const rect = {
        left: Math.max(0, geometry.window.left + i),
        top: Math.max(0, geometry.window.top + i),
        right: Math.min(frameWidth, geometry.window.right - i),
        bottom: Math.min(frameHeight, geometry.window.bottom - i),
    };
    if (rect.right <= rect.left || rect.bottom <= rect.top)
        return null;
    return rect;
}
/** Clip a rectangle to the frame; `null` when nothing is left. */
export function ssClipToFrame(rect, frameWidth = SS_FRAME_WIDTH, frameHeight = SS_FRAME_HEIGHT) {
    const out = {
        left: Math.max(0, rect.left),
        top: Math.max(0, rect.top),
        right: Math.min(frameWidth, rect.right),
        bottom: Math.min(frameHeight, rect.bottom),
    };
    if (out.right <= out.left || out.bottom <= out.top)
        return null;
    return out;
}
/**
 * Alpha of the foreground art for a cut-source luma (8-bit code / 255).
 * Linear key, measured: the 50 % point sits at key = clip/1000 and the slope
 * is 0.375 * 1000^(gain/1000) per unit key, so gain 1000 is a hard key.
 * Pre-multiplied keys use the key as-is and ignore clip, gain and invert.
 */
export function ssArtKeyAlpha(key, art) {
    if (art.premultiplied)
        return clamp(key, 0, 1);
    const slope = 0.375 * Math.pow(1000, art.gain / 1000);
    const alpha = clamp(0.5 + slope * (key - art.clip / 1000), 0, 1);
    return art.invert ? 1 - alpha : alpha;
}
/** Penalty weight of a frame-aligned edge that fails to cover the frame */
const VIOLATION_WEIGHT = 100;
function solveAxis(axis, W, target, allowCrop, margin) {
    const frame = axis === "x" ? SS_FRAME_WIDTH : SS_FRAME_HEIGHT;
    const span = axis === "x" ? W : ssWindowHeight(W);
    const picture = axis === "x" ? W : (W * 9) / 16;
    const limit = axis === "x" ? SS_POSITION_X_LIMIT : SS_POSITION_Y_LIMIT;
    const loSide = axis === "x" ? "left" : "top";
    const hiSide = axis === "x" ? "right" : "bottom";
    const cropAxis = axis === "x" ? "h" : "v";
    const userLo = allowCrop ? target.cropLo : 0;
    const userHi = allowCrop ? target.cropHi : 0;
    // Centre the available (user-cropped) part of the picture on the target.
    const centre = (target.lo + target.hi) / 2 - (picture * (userLo - userHi)) / 2;
    const ideal = axis === "x"
        ? ((centre - SS_FRAME_WIDTH / 2) * 5) / 3
        : ((SS_FRAME_HEIGHT / 2 - centre) * 5) / 3;
    const minLo = roundEven(userLo * picture);
    const minHi = roundEven(userHi * picture);
    const goalLo = target.flushLo ? Math.min(target.lo, -margin) : target.lo;
    const goalHi = target.flushHi
        ? Math.max(target.hi, frame + margin)
        : target.hi;
    // The window lands exactly where the switcher's float32 chain puts it, so
    // every candidate position is predictable and usable.
    const loEdge = (pos) => ssQuantiseEdge(axis === "x" ? ssRawLeft(pos, W) : ssRawTop(pos, W));
    // A wide margin (border on) moves the picture's centre off the target's
    // centre on a one-sided flush axis; search around both.
    const goalCentre = (goalLo + goalHi) / 2 - (picture * (userLo - userHi)) / 2;
    const goalIdeal = axis === "x"
        ? ((goalCentre - SS_FRAME_WIDTH / 2) * 5) / 3
        : ((SS_FRAME_HEIGHT / 2 - goalCentre) * 5) / 3;
    let best = null;
    const from = Math.round(Math.min(ideal, goalIdeal)) - 6;
    const to = Math.round(Math.max(ideal, goalIdeal)) + 6;
    for (let pos = from; pos <= to; pos++) {
        if (pos < -limit || pos > limit)
            continue;
        const lo = loEdge(pos);
        const hi = lo + span;
        // Crop in output px, then in protocol units.
        let pxLo = 0;
        let pxHi = 0;
        if (allowCrop) {
            pxLo = target.flushLo
                ? floorEven(goalLo - lo)
                : roundEven(target.lo - lo);
            pxHi = target.flushHi
                ? floorEven(hi - goalHi)
                : roundEven(hi - target.hi);
            pxLo = Math.max(0, minLo, pxLo);
            pxHi = Math.max(0, minHi, pxHi);
        }
        const unitsLo = ssCropUnitsForPx(pxLo, W, loSide);
        const unitsHi = ssCropUnitsForPx(pxHi, W, hiSide);
        const winLo = lo + fe(ssCropPx(unitsLo, W, cropAxis));
        const winHi = hi - rhe(ssCropPx(unitsHi, W, cropAxis));
        if (winHi <= winLo)
            continue;
        let violation = 0;
        let error = 0;
        if (target.flushLo)
            violation += Math.max(0, winLo - goalLo) ** 2;
        else
            error += Math.abs(winLo - target.lo);
        if (target.flushHi)
            violation += Math.max(0, goalHi - winHi) ** 2;
        else
            error += Math.abs(winHi - target.hi);
        const score = violation * VIOLATION_WEIGHT + error + Math.abs(pos - ideal) * 1e-3;
        if (!best || score < best.score) {
            best = { pos, cropLo: unitsLo, cropHi: unitsHi, score };
        }
    }
    return (best ?? {
        pos: clamp(Math.round(ideal), -limit, limit),
        cropLo: 0,
        cropHi: 0,
        score: Number.POSITIVE_INFINITY,
    });
}
/**
 * Crop units that move a window edge in by `px` output pixels (even) for a
 * window `windowWidth` px wide, checked against the switcher's float32 crop
 * (left/top floor to even, right/bottom round to even). Picks the value in
 * the middle of the range that gives exactly `px`, so it is robust; returns
 * the nearest reachable value when `px` cannot be hit (beyond the range).
 */
export function ssCropUnitsForPx(px, windowWidth, side) {
    if (px <= 0)
        return 0;
    const horizontal = side === "left" || side === "right";
    const max = horizontal ? SS_CROP_LR_MAX : SS_CROP_TB_MAX;
    const floors = side === "left" || side === "top";
    const axis = horizontal ? "h" : "v";
    const lands = (units) => {
        const raw = ssCropPx(units, windowWidth, axis);
        return floors ? fe(raw) : rhe(raw);
    };
    // 1 px = 32000 / W units; aim at the middle of the px..px+2 (floor) or
    // px-1..px+1 (round) range.
    const perPx = 32000 / windowWidth;
    const aim = clamp(Math.round((floors ? px + 1 : px) * perPx), 0, max);
    if (lands(aim) === px)
        return aim;
    for (let step = 1; step <= Math.ceil(2 * perPx) + 2; step++) {
        for (const units of [aim - step, aim + step]) {
            if (units >= 0 && units <= max && lands(units) === px)
                return units;
        }
    }
    return aim;
}
/**
 * Protocol values that place a box on the target rectangle as exactly as the
 * switcher allows.
 *
 * - Edges aligned with the frame edge are pushed {@link ssCleanEdgeMargin}
 *   px past it (4, more with an inner border or soft border edges), so the
 *   soft edge and the border land off screen and the box covers the frame
 *   cleanly. A size-1000 box cannot go wider than the frame, so a full-width
 *   (or full-height) box still shows the 1.5 px soft edge on that axis.
 * - Inner edges land on the nearest even pixel.
 * - Non-16:9 targets are cropped ("cover" fit); with `allowCrop: false` the
 *   window is the whole 16:9 picture.
 * - Targets smaller than the switcher's minimum size are reached with crop.
 * - A target with no area (width or height 0) is disabled.
 */
export function ssFitBox(target, options = {}) {
    const allowCrop = options.allowCrop ?? true;
    const margin = options.edgeMargin ?? ssCleanEdgeMargin(options.border);
    const tol = options.flushTolerance ?? 1;
    const ct = allowCrop ? clamp(target.cropTop ?? 0, 0, 1) : 0;
    const cb = allowCrop ? clamp(target.cropBottom ?? 0, 0, 1) : 0;
    const cl = allowCrop ? clamp(target.cropLeft ?? 0, 0, 1) : 0;
    const cr = allowCrop ? clamp(target.cropRight ?? 0, 0, 1) : 0;
    const left = (target.x - target.width / 2) * SS_FRAME_WIDTH;
    const right = (target.x + target.width / 2) * SS_FRAME_WIDTH;
    const top = (target.y - target.height / 2) * SS_FRAME_HEIGHT;
    const bottom = (target.y + target.height / 2) * SS_FRAME_HEIGHT;
    const tx = {
        lo: left,
        hi: right,
        flushLo: left <= tol,
        flushHi: right >= SS_FRAME_WIDTH - tol,
        cropLo: cl,
        cropHi: cr,
    };
    const ty = {
        lo: top,
        hi: bottom,
        flushLo: top <= tol,
        flushHi: bottom >= SS_FRAME_HEIGHT - tol,
        cropLo: ct,
        cropHi: cb,
    };
    const nominalSize = clamp(Math.round(Math.max(target.width, target.height) * 1000), SS_SIZE_MIN, SS_SIZE_MAX);
    const disabled = (pos) => ({
        enabled: false,
        x: pos?.x ?? 0,
        y: pos?.y ?? 0,
        size: nominalSize,
        cropped: false,
        cropTop: 0,
        cropBottom: 0,
        cropLeft: 0,
        cropRight: 0,
    });
    const nominalPos = {
        x: clamp(Math.round((target.x - 0.5) * 2 * SS_POSITION_X_EDGE), -SS_POSITION_X_LIMIT, SS_POSITION_X_LIMIT),
        y: clamp(Math.round((0.5 - target.y) * 2 * SS_POSITION_Y_EDGE), -SS_POSITION_Y_LIMIT, SS_POSITION_Y_LIMIT),
    };
    if (target.enabled === false ||
        !(target.width * SS_FRAME_WIDTH >= 1) ||
        !(target.height * SS_FRAME_HEIGHT >= 1) ||
        cl + cr >= 1 ||
        ct + cb >= 1) {
        return disabled(nominalPos);
    }
    // Desired window including the frame-edge margin.
    const wantLo = tx.flushLo ? Math.min(tx.lo, -margin) : tx.lo;
    const wantHi = tx.flushHi ? Math.max(tx.hi, SS_FRAME_WIDTH + margin) : tx.hi;
    const wantTop = ty.flushLo ? Math.min(ty.lo, -margin) : ty.lo;
    const wantBottom = ty.flushHi
        ? Math.max(ty.hi, SS_FRAME_HEIGHT + margin)
        : ty.hi;
    const kNeeded = Math.max((wantHi - wantLo) / (SS_FRAME_WIDTH * (1 - cl - cr)), (wantBottom - wantTop) / (SS_FRAME_HEIGHT * (1 - ct - cb)));
    // Without crop the window is the picture, so only the 16:9 size matters.
    const s0 = clamp(Math.ceil(kNeeded * 1000 - EPS), SS_SIZE_MIN, SS_SIZE_MAX);
    let best = null;
    for (let size = s0 - 3; size <= s0 + 4; size++) {
        if (size < SS_SIZE_MIN || size > SS_SIZE_MAX)
            continue;
        const W = ssWindowWidth(size);
        const ax = solveAxis("x", W, tx, allowCrop, margin);
        const ay = solveAxis("y", W, ty, allowCrop, margin);
        // Prefer the smallest zoom that does the job.
        const score = ax.score + ay.score + Math.abs(size - kNeeded * 1000) * 1e-2;
        if (!best || score < best.score)
            best = { size, ax, ay, score };
    }
    if (!best || !Number.isFinite(best.score))
        return disabled(nominalPos);
    const cropped = best.ax.cropLo > 0 ||
        best.ax.cropHi > 0 ||
        best.ay.cropLo > 0 ||
        best.ay.cropHi > 0;
    return {
        enabled: true,
        x: best.ax.pos,
        y: best.ay.pos,
        size: best.size,
        cropped,
        cropLeft: best.ax.cropLo,
        cropRight: best.ax.cropHi,
        cropTop: best.ay.cropLo,
        cropBottom: best.ay.cropHi,
    };
}
/**
 * Read a box back into normalized terms: the window as seen on screen
 * (clipped to the frame), and the switcher's crop as fractions of the source.
 * {@link ssFitBox} of the result reproduces the same on-screen window.
 */
export function ssBoxToTarget(box) {
    const g = ssBoxGeometry(box);
    const clipped = g.empty ? null : ssClipToFrame(g.window);
    if (!clipped || box.enabled === false) {
        const cx = SS_FRAME_WIDTH / 2 + (3 * box.x) / 5;
        const cy = SS_FRAME_HEIGHT / 2 - (3 * box.y) / 5;
        return {
            enabled: false,
            x: cx / SS_FRAME_WIDTH,
            y: cy / SS_FRAME_HEIGHT,
            width: g.width / SS_FRAME_WIDTH,
            height: g.width / SS_FRAME_WIDTH,
            cropTop: 0,
            cropBottom: 0,
            cropLeft: 0,
            cropRight: 0,
        };
    }
    const pictureHeight = (g.width * 9) / 16;
    const fraction = (v) => clamp(v, 0, 1);
    return {
        enabled: true,
        x: (clipped.left + clipped.right) / 2 / SS_FRAME_WIDTH,
        y: (clipped.top + clipped.bottom) / 2 / SS_FRAME_HEIGHT,
        width: (clipped.right - clipped.left) / SS_FRAME_WIDTH,
        height: (clipped.bottom - clipped.top) / SS_FRAME_HEIGHT,
        // Crop from the real window, not the clipped one: the part past the
        // frame edge is the clean-edge margin, not something the source lacks.
        cropLeft: fraction((g.window.left - g.bounds.left) / g.width),
        cropRight: fraction(1 - (g.window.right - g.bounds.left) / g.width),
        cropTop: fraction((g.window.top - g.bounds.top) / pictureHeight),
        cropBottom: fraction(1 - (g.window.bottom - g.bounds.top) / pictureHeight),
    };
}
